import { Dropdown } from "antd";
import React, { useState } from "react";
import { BellFilledIcon, BellOutlinedIcon, BellIcon, BellMessageIcon, BellHalfFilledIconsvg } from "../../Core/svgV2/BellIcon";
import { DotsVerticalIcon } from "../../Core/svgV2/DotsVertical";
import { PinnedIcon, PinnedOutlinedIcon, UnpinIcon } from "../../Core/svgV2/Pinned";
import { EllipseIcon } from "../../Core/svgV2/Ellipse";
import { DeleteIcon } from "../../Core/svgV2/DeleteIcon";
import { useAppDispatch, useAppSelector } from "../../Core/redux/hooks";
import {
  resetConversations,
  updateConversations,
  updateConversationsMultiple,
  updateThreadForUser,
} from "../../Core/redux/slices/conversations.slice";
import { getThreadsMembers } from "../../Core/redux/api/v3/conversationAPI";
import dayjs from "dayjs";
import CustomAvatar from "../../Core/CommonV2/CustomAvatar";
import { chatClient, client } from "../../Core/utils/axiosClient";
import ParseMentions from "./ParseMentions";
import { EditPencilIcon } from "../../Core/svgV2/EditIcon";
import { BellHalfFilledIcon } from "../../Core/svgV3/BellHalfFilledIcon";
import { UnPinIcon } from "../../Core/svgV3/UnPinIcon";
import TickIcon, { DoubleTickIcon, SingleTickIcon } from "../../Core/svgV3/TickIcon";
import { PhotoIcon } from "../../Core/svgV3/PhotoIcon";
import { Videov3Icon } from "../../Core/svgV3/VideoIcon";
import { DocumentIcon } from "../../Core/svgV3/DocumentIcon";

const defaultFormatTime = (ts: string | null | undefined): string => {
  if (!ts) return "";
  const d = dayjs(ts);
  if (!d.isValid()) return "";
  if (d.isSame(dayjs(), "day")) return d.local().format("h:mm A");
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return d.local().format("D MMM");
};

const MEDIA_ICON_MAP = {
  photo: PhotoIcon,
  image: PhotoIcon,
  picture: PhotoIcon,
  video: Videov3Icon,
  document: DocumentIcon,
  file: DocumentIcon,
  pdf: DocumentIcon,
};

const getLastMessageMediaIcon = (thread: any) => {
  const rawType =
    thread.lastMessageAttachmentType ||
    thread.lastAttachmentType ||
    thread.attachmentType ||
    thread.lastMessageType;

  const previewText = (thread.lastMessage || thread.lastActivity || "")
    .toString()
    .trim()
    .toLowerCase();

  const key = (rawType || previewText || "").toString().trim().toLowerCase();

  return MEDIA_ICON_MAP[key] || null;
};
// ────────────────────────────────────────────────────────────────────────

const ChatCard = ({
  thread,
  socket,
  showMoreOptions = true,
  onChatClickCallBack,
  formatTime = defaultFormatTime,
}: {
  thread?: any;
  socket?: any;
  showMoreOptions?: boolean;
  onChatClickCallBack?: (val: any) => void;
  formatTime?: (ts: string | null | undefined) => string;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.userDetails);
  const { currentThread } = useAppSelector((state) => state.conversations);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ── Field normalisation ──────────────────────────────────────────────────
  const isGroup = thread.isGroup === true;

  const displayName = isGroup
    ? thread.name
    : thread.firstname && thread.lastname
      ? `${thread.firstname} ${thread.lastname}`.trim()
      : thread.name;

  const avatarSrc = isGroup
    ? thread.groupProfilePicture || null
    : thread.profilePicture || thread.profile_picture || null;

  const avatarColor = isGroup
    ? thread.threadProfilePictureColorCode || null
    : thread.profilePictureColorCode || null;

  const createdById = thread.createdBy || thread.createdById;
  const isAdmin = isGroup && String(createdById) === String(user.id);

  const threadId = thread.threadId || thread.id;
  const otherUserId = thread.userid || thread.userId;

  const lastTime = thread.lastMessageUpdate || thread.lastMessageTime;
  const timeLabel = formatTime(lastTime);
  const MediaIcon = getLastMessageMediaIcon(thread);
  const { lastActivity, lastActivityType, lastActivityReactorId } = useAppSelector((state) => ({
    lastActivity: state.conversations.allChatsLastActivity?.[String(threadId)],
    lastActivityType: state.conversations.allChatsLastActivityType?.[String(threadId)],
    lastActivityReactorId: state.conversations.allChatsReactorId?.[String(threadId)],
  }));

  const reactionText = React.useMemo(() => {
    if (lastActivityType !== "reaction" || !lastActivity) return lastActivity;
    const isMe = String(user.id) === String(lastActivityReactorId);
    if (!isMe) return lastActivity;
    return lastActivity.replace(/^.+?(?= reacted)/, "You");
  }, [lastActivity, lastActivityType, user.id, lastActivityReactorId]);
  const { isLastMessageMine, tickState } = useAppSelector((state) => {
    const liveMessages = state.conversations.allChatMessages?.[String(threadId)];
    const liveLast = liveMessages?.[liveMessages.length - 1];

    // Prefer live socket-updated message when available (chat opened this session)
    if (liveLast && !liveLast.activity) {
      const senderId = liveLast.sender ?? liveLast.senderId;
      if (senderId == null || String(senderId) !== String(user.id)) {
        return { isLastMessageMine: false, tickState: null as null | "sent" | "delivered" | "read" };
      }
      const row = state.conversations.threads.find((t: any) => String(t.threadId) === String(threadId));
      const others = (row?.participants ?? []).filter((p: any) => +p !== +user.id);

      const readByAll =
        others.length > 0 &&
        others.every((uid: any) =>
          (liveLast.messageReadBy ?? []).some((r: any) => String(r.userId) === String(uid))
        );
      if (readByAll) return { isLastMessageMine: true, tickState: "read" as const };

      const deliveredByAll =
        others.length > 0 &&
        others.every((uid: any) =>
          (liveLast.messageReceivedBy ?? []).some((r: any) => String(r.userId) === String(uid))
        );
      return { isLastMessageMine: true, tickState: deliveredByAll ? ("delivered" as const) : ("sent" as const) };
    }

    // Fallback: server-seeded meta from getThreads — correct even before the chat's been opened
    const meta = state.conversations.allChatsLastMessageMeta?.[String(threadId)];
    if (!meta || meta.senderId == null || meta.senderId !== String(user.id)) {
      return { isLastMessageMine: false, tickState: null };
    }
    // Read implies delivered — don't let a stale deliveredByAll=false override a true read
    const tick = meta.readByAll ? "read" : meta.deliveredByAll ? "delivered" : "sent";
    return { isLastMessageMine: true, tickState: tick as "sent" | "delivered" | "read" };
  });
  // ────────────────────────────────────────────────────────────────────────

  const handlePinChat = async () => {
    dispatch(updateThreadForUser({
      threadId, userId: otherUserId, isPinned: +!thread.isPinned, isThread: threadId,
    }));
    if (threadId) {
      await client.put("/conversation/update-thread-for-user", {
        threadId, userId: user.id, isPinned: +!thread.isPinned,
      });
    } else {
      await client.put("/conversation/private-chat", {
        receiverId: otherUserId, senderId: user.id, isPinned: +!thread.isPinned,
      });
    }
  };

  const handleMuteChat = async () => {
    dispatch(updateThreadForUser({
      threadId, userId: otherUserId, isMute: +!thread.isMute, isThread: threadId,
    }));
    if (threadId) {
      await client.put("/conversation/update-thread-for-user", {
        threadId, userId: user.id, isMute: +!thread.isMute,
      });
    } else {
      await client.put("/conversation/private-chat", {
        receiverId: otherUserId, senderId: user.id, isMute: +!thread.isMute,
      });
    }
  };

  const handleViewDetails = () => {
    dispatch(updateConversations({
      key: isGroup ? "groupDetailsDrawer" : "userDetailsDrawer",
      value: { visible: true, data: thread },
    }));
  };

  const handleDeleteThread = async () => {
    dispatch(updateConversationsMultiple([
      { key: "deleteThread", value: true },
      { key: "deleteThreadDetails", value: thread },
    ]));
  };

  const handleEditThread = () => {
    dispatch(updateConversationsMultiple([
      { key: "editThread", value: true },
      { key: "editThreadDetails", value: currentThread },
    ]));
  };

  const items = [
    {
      key: "1",
      label: <div onClick={handleViewDetails} className="text-gray-700 out-300-14">View details</div>,
      icon: <EllipseIcon className="text-[#165ABF] w-[12.8px] h-[12.8px] mt-[1.6px] ml-[1.6px]" />,
    },
    {
      key: "2",
      label: (
        <div onClick={handlePinChat} className="text-gray-700 out-300-14">
          {thread.isPinned == 1 ? "Unpin chat" : "Pin chat"}
        </div>
      ),
      icon: thread.isPinned == 1
        ? <UnPinIcon className="text-[#165ABF] w-[12.8px] h-[12.8px] mt-[1.6px] ml-[1.6px]" />
        : <PinnedIcon className="text-[#165ABF] w-[12.8px] h-[12.8px] mt-[1.6px] ml-[1.6px]" />,
    },
    {
      key: "3",
      label: (
        <div onClick={handleMuteChat} className="text-gray-700 out-300-14">
          {thread.isMute == 1 ? "Unmute notifications" : "Mute notifications"}
        </div>
      ),
      icon: thread.isMute == 1
        ? <BellMessageIcon className="text-[#165ABF] w-[12.8px] h-[12.8px] mt-[1.6px] ml-[1.6px]" />
        : <BellHalfFilledIcon className="text-[#165ABF] w-[12.8px] h-[12.8px] mt-[1.6px] ml-[1.6px]" />,
    },
    // isGroup && isAdmin && {
    //   key: "4",
    //   label: <div onClick={handleEditThread} className="out-300-14 text-gray-700">Edit group</div>,
    //   icon: <EditPencilIcon className="text-[#165ABF] w-[12.8px] h-[12.8px] mt-[1.6px] ml-[1.6px]" />,
    // },
    // isGroup && isAdmin && { key: "4-divider", type: "divider" },
    // isGroup && isAdmin && {
    //   key: "5",
    //   label: (
    //     <div
    //       className="flex text-gray-700 gap-x-2 hover:text-error-600"
    //       role="button"
    //       onClick={handleDeleteThread}
    //     >
    //       <DeleteIcon className="" />
    //       <div className="out-300-14">Delete group</div>
    //     </div>
    //   ),
    //   danger: true,
    // },
  ].filter(Boolean);

  const joinThread = async (t) => {
    dispatch(updateConversationsMultiple([
      { value: { ...t, type: "group", limit: 20, offset: 0 }, key: "currentThread" },
      { key: "currentThreadMessage", value: t.messages?.length > 0 ? t.messages : [] },
    ]));
    dispatch(resetConversations(["replyMessage", "editMessage"]));
    socket.emit("joinroom", {
      newThread: threadId,
      prevThread: currentThread.threadId,
      joiningUserId: user.id,
    });
    await dispatch(getThreadsMembers({ threadId }));
    if (onChatClickCallBack && typeof onChatClickCallBack === "function") {
      onChatClickCallBack(true);
    }
  };

  const joinPrivateThread = async (t) => {
    dispatch(updateConversationsMultiple([
      { value: { ...t, type: "private", limit: 20, offset: 0 }, key: "currentThread" },
      { key: "currentThreadMessage", value: t.messages?.length > 0 ? t.messages : [] },
    ]));
    dispatch(resetConversations(["replyMessage", "editMessage"]));

    await chatClient.post("/conversation/read-messages", {
      userId: user.id,
      chatId: threadId,
      user,
    });

    await chatClient.post("/conversation/read-messages", {
      userId: user.id,
      chatId: threadId,
      user,
    });


    if (onChatClickCallBack && typeof onChatClickCallBack === "function") {
      onChatClickCallBack(true);
    }
  };

  const getAvatar = () => {
    return (
      <div className="relative flex-shrink-0">
        <CustomAvatar
          src={avatarSrc}
          title={displayName}
          size={48}
          badge={!isGroup}
          fontSize="20px"
          userId={otherUserId}
          whiteText
          color={avatarColor}
        />
      </div>
    );
  };

  const hasActiveThread = Boolean(
    currentThread.threadId || currentThread.userId || currentThread.userid
  );
  const isSelected =
    hasActiveThread &&
    (currentThread.threadId
      ? String(currentThread.threadId) === String(threadId)
      : String(currentThread.userId || currentThread.userid) === String(otherUserId));

  return (
    <div
      className={`flex border-b cursor-pointer border-gray-200 flex-row items-center group hover:bg-gray-50 transition-colors duration-150 ease-out h-[80px] w-full py-[16px] pl-[24px] pr-[14px] ${isSelected ? "bg-gray-50" : "bg-[#F9FBFE]"
        }`}
    >
      {getAvatar()}

      <div
        onClick={() => (isGroup ? joinThread(thread) : joinPrivateThread(thread))}
        className="flex flex-col flex-1 min-w-0 items-start gap-[6px] ml-[12px]"
      >
        {/* Row 1: name on the left, time + three-dot menu opposite it on the right */}
        <div className="flex w-full items-center justify-between gap-x-1 h-[20px]">
          <div className="flex items-center min-w-0 flex-1 gap-x-[6px]">
            {/* Name */}
            <span className="font-[500] whitespace-nowrap truncate text-[14px] leading-[20px]" style={{ color: "#292927" }}>
              {displayName}
            </span>
          </div>

          {/* Time + three-dot menu */}
          <div className="flex items-center gap-x-2 flex-shrink-0 h-[20px]">
            {timeLabel && (
              <span
                className={`text-[12px] leading-[18px] whitespace-nowrap ${+thread.unreadCount > 0 ? "text-[#165ABF]" : "text-[#667085]"
                  }`}
              >
                {timeLabel}
              </span>
            )}
            {showMoreOptions && (
              <div
                className={`flex-shrink-0 w-[16px] h-[16px] flex items-center justify-center group-hover:block ${dropdownOpen ? "block" : "hidden"
                  }`}
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  onOpenChange={(e) => setDropdownOpen(e)}
                  dropdownRender={(menu) => <div className="w-[200px]">{menu}</div>}
                  menu={{ items }}
                  trigger={["click"]}
                  overlayClassName="chatcard-menu-fade"
                >
                  <button className="flex items-center justify-center w-[16px] h-[16px]">
                    <DotsVerticalIcon className="text-gray-400 w-[16px] h-[16px]" />
                  </button>
                </Dropdown>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: last message preview on the left, pin/mute + mention + unread badge on the right.
            Pin/mute icons sit immediately before the unread count. When there's no unread
            count, they simply occupy that slot instead (no empty gap left behind). */}
        <div className="flex items-center justify-between w-full gap-x-2">
          <div className="flex-1 min-w-0 flex items-center gap-x-1">
            {lastActivityType === "reaction" ? (
              <span className="leading-[20px] text-[14px] font-[300] text-gray-500 truncate block">
                {reactionText}
              </span>
            ) : (
              <>
                {isLastMessageMine && (
                  <span className="flex-shrink-0 flex items-center justify-center w-[18px] h-[18px]">
                    {tickState === "read" ? (
                      <DoubleTickIcon className="text-[#FEB273]" width={20} height={20} />
                    ) : tickState === "delivered" ? (
                      <DoubleTickIcon className="text-[#8A9BB0]" width={20} height={20} />
                    ) : (
                      <SingleTickIcon className="text-[#8A9BB0]" width={20} height={20} />
                    )}
                  </span>
                )}
                {MediaIcon && (
                  <span className="flex-shrink-0 flex items-center justify-center w-[16px] h-[16px] text-gray-400">
                    <MediaIcon width={16} height={16} />
                  </span>
                )}
                <ParseMentions
                  value={
                    (thread.lastMessage || thread.lastActivity)
                      ?.slice(0, 40)
                      ?.replace(/\n/g, " ") || ""
                  }
                  textClassName="leading-[20px] text-[14px] font-[300] text-gray-500 truncate block"
                  clickDisable
                />
              </>
            )}
          </div>
          <div className="flex items-center gap-x-1 flex-shrink-0">
            {thread.isMentioned ? (
              <span className="text-[#165ABF] out-500-14">@</span>
            ) : null}
            {thread.isPinned ? (
              <span className="flex-shrink-0">
                <PinnedIcon className="text-gray-400" />
              </span>
            ) : null}
            {thread.isMute ? (
              <span className="flex-shrink-0">
                <BellHalfFilledIconsvg className="text-gray-500" />
              </span>
            ) : null}
            {+thread.unreadCount > 0 ? (
              <div className="flex min-w-[18px] h-[18px] px-1 bg-[#165ABF] rounded-[32px] items-center justify-center text-white text-[12px] font-[500]">
                {+thread.unreadCount > 999 ? "999+" : thread.unreadCount}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatCard;